import { Alert, Input, Modal, Typography } from 'antd';
import { useState } from 'react';
import type { ReactNode } from 'react';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [LIFECYCLE] ONE CONFIRMATION FOR EVERY DESTRUCTIVE ACT IN THE APP.
 *
 *  ⚠️ A CONTROLLED `<Modal>`, NOT `modal.confirm`. CLAUDE.md §8: a required, validated input
 *  needs a controlled modal, because `modal.confirm`'s `content` cannot own state that its
 *  own OK button reads. `PublishPolicyModal` and `pages/DutyFill` are the precedents.
 *
 *  ⚠️ AND IT IS ONE COMPONENT FOR FIVE LISTS AND A DETAIL PAGE, WHICH IS ALSO A CENSUS
 *  DECISION. `e2e/specs/census.spec.ts` counts `<Modal` occurrences PER FILE against an
 *  exact exemption; `pages/Arrangements/index.tsx` is already exempted at exactly 2, so a
 *  sixth screen growing its own dialog would fail the sweep. One shared file is one swept
 *  site for all of them.
 *
 *  ⚠️ `requireTyped` IS USED TWICE AND ONLY TWICE: archiving a PUBLISHED arrangement (type
 *  its reference) and any permanent delete. Everything else confirms with a button, because
 *  a confirmation that always demands typing is one an operator learns to defeat rather than
 *  to read.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const ConfirmDestructive = ({
  open,
  title,
  body,
  okText,
  requireTyped,
  confirmLoading,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  okText: string;
  /** When set, OK stays disabled until the operator types this exactly. */
  requireTyped?: string;
  confirmLoading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const [typed, setTyped] = useState('');

  /*
   * ⚠️ THE RESET LIVES IN THE COMMIT **AND** THE DISMISSAL, IN THIS COMPONENT, so no
   * consumer can forget it — which is the rule this pass derived from the
   * `CopyCommandRolesModal` defect, applied to the one place that can enforce it. This
   * component is mounted by its page at all times, so a buffer left behind would outlive
   * every close and greet the next operator pre-filled.
   *
   * ⚠️ NOT AN EFFECT KEYED ON `open`. `setState` in an effect body is a cascading render and
   * `eslint-plugin-react-hooks` refuses it — correctly: the reset is a consequence of an
   * INTERACTION, not a synchronisation with anything outside React.
   */
  const dismiss = () => {
    setTyped('');
    onCancel();
  };

  const commit = () => {
    setTyped('');
    onConfirm();
  };

  const satisfied = requireTyped === undefined || typed.trim() === requireTyped;

  return (
    <Modal
      open={open}
      title={title}
      okText={okText}
      okButtonProps={{ danger: true, disabled: !satisfied }}
      confirmLoading={confirmLoading ?? false}
      onCancel={dismiss}
      onOk={commit}
      data-testid="confirm-destructive"
    >
      <div className="flex flex-col gap-3">
        {body}
        {requireTyped !== undefined && (
          <div className="flex flex-col gap-1">
            <Typography.Text type="secondary" className="text-xs">
              Type <strong>{requireTyped}</strong> to confirm.
            </Typography.Text>
            <Input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder={requireTyped}
              data-testid="confirm-typed"
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

/** The body of an archive confirmation, so five pages phrase the same act the same way. */
export const ArchiveNote = ({ children }: { children: ReactNode }) => (
  <Alert type="warning" showIcon message={children} />
);
